// src/components/PWAInstallBanner.js
// B2B PWA install prompt — listens for beforeinstallprompt (Android/Chrome),
// shows manual instructions on iOS Safari, hides when already in standalone mode.

import React, { useState, useEffect } from 'react';

const isIOSDevice = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode() || localStorage.getItem('trm_pwa_installed')) {
      setInstalled(true);
      return;
    }

    if (localStorage.getItem('trm_pwa_dismissed')) return;

    if (isIOSDevice()) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      localStorage.setItem('trm_pwa_installed', '1');
      setInstalled(true);
      setVisible(false);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstalled(true);
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('trm_pwa_dismissed', '1');
  };

  if (!visible || installed) return null;

  if (isIOS) {
    return (
      <>
        <div className="pwa-banner pwa-banner--ios" role="banner">
          <div className="pwa-banner__icon" aria-hidden="true">📲</div>
          <div className="pwa-banner__text">
            <button
              className="pwa-banner__title pwa-banner__title--link"
              onClick={() => setShowIOSModal(true)}
            >
              Installa l'app sul tuo dispositivo
            </button>
          </div>
          <button
            className="pwa-banner__close"
            onClick={handleDismiss}
            aria-label="Chiudi banner installazione"
          >
            ✕
          </button>
        </div>

        {showIOSModal && (
          <div className="pwa-modal-overlay" onClick={() => setShowIOSModal(false)}>
            <div className="pwa-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pwa-modal__header">
                <span>📲 Come installare l'app</span>
                <button className="pwa-banner__close" onClick={() => setShowIOSModal(false)}>✕</button>
              </div>
              <ol className="pwa-modal__steps">
                <li>Tocca il pulsante di condivisione <strong>
                  <svg className="pwa-share-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="icona condividi">
                    <polyline points="8 6 12 2 16 6"/>
                    <line x1="12" y1="2" x2="12" y2="15"/>
                    <path d="M20 15v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4"/>
                  </svg>
                </strong> (icona quadrata con freccia verso l'alto)</li>
                <li>Scorri in basso nel menu e seleziona <strong>"Aggiungi alla schermata Home".</strong><br/>
                  Se non lo vedi, scorri in fondo e tocca "Modifica azioni" per aggiungerlo.
                </li>
              </ol>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="pwa-banner" role="banner">
      <div className="pwa-banner__icon" aria-hidden="true">📲</div>
      <div className="pwa-banner__text">
        <p className="pwa-banner__title">
          Installa l'app sul tuo dispositivo
        </p>
        <p className="pwa-banner__sub">
          Accesso rapido · Ideale per tablet, iPad e smartphone
        </p>
      </div>
      <div className="pwa-banner__actions">
        <button
          className="pwa-banner__btn"
          onClick={handleInstall}
          aria-label="Installa applicazione"
        >
          Installa
        </button>
        <button
          className="pwa-banner__close"
          onClick={handleDismiss}
          aria-label="Chiudi banner installazione"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
